import { LogLevel, type Environment, type ILogger, type LogPayload, type Metadata } from '../types/logger'
import { DEFAULT_DOMAIN, LOG_FORWARDER_URL, SERVICE_NAME, TEAM_TAG } from '../constants/logger'

function getEnvironment(): Environment {
  const ua = navigator.userAgent

  // Use the User-Agent Client Hints API when available (Chromium browsers),
  // fall back to lightweight regex parsing for Safari / Firefox.
  const uaData = 'userAgentData' in navigator ? (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData : undefined

  const browser = parseBrowser(ua, uaData)
  const os = parseOS(ua, uaData)

  return {
    browser,
    device: parseDevice(ua),
    engine: parseEngine(ua),
    os,
  }
}

interface NavigatorUAData {
  readonly brands: ReadonlyArray<{ brand: string; version: string }>
  readonly mobile: boolean
  readonly platform: string
}

function parseBrowser(ua: string, uaData?: NavigatorUAData): { name: string; version: string } {
  // Prefer Client Hints – pick the first brand that isn't a GREASE value
  if (uaData?.brands?.length) {
    const meaningful = uaData.brands.find((b) => !b.brand.includes('Not') && !b.brand.includes('Chromium'))
    if (meaningful) return { name: meaningful.brand, version: meaningful.version }
    const chromium = uaData.brands.find((b) => b.brand === 'Chromium')
    if (chromium) return { name: chromium.brand, version: chromium.version }
  }

  // Regex fallback – order matters (more specific first)
  const patterns: [string, RegExp][] = [
    ['Edge', /Edg(?:e|A|iOS)?\/(\S+)/],
    ['Opera', /(?:OPR|Opera)\/(\S+)/],
    ['Samsung Internet', /SamsungBrowser\/(\S+)/],
    ['Firefox', /(?:FxiOS|Firefox)\/(\S+)/],
    ['Chrome', /(?:CriOS|Chrome)\/(\S+)/],
    ['Safari', /Version\/(\S+).*Safari/],
  ]

  for (const [name, re] of patterns) {
    const m = ua.match(re)
    if (m) return { name, version: m[1] ?? '' }
  }

  return { name: '', version: '' }
}

function parseOS(ua: string, uaData?: NavigatorUAData): { name: string; version: string } {
  if (uaData?.platform) {
    // Client Hints gives us the platform name but not the version at the
    // low-entropy level. Extract the version from the UA string instead.
    const versionPatterns: Record<string, RegExp> = {
      Windows: /Windows NT ([\d.]+)/,
      macOS: /Mac OS X ([\d_.]+)/,
      Android: /Android ([\d.]+)/,
      'Chrome OS': /CrOS \S+ ([\d.]+)/,
    }
    const re = versionPatterns[uaData.platform]
    const version = re ? (ua.match(re)?.[1]?.replace(/_/g, '.') ?? '') : ''
    return { name: uaData.platform, version }
  }

  const osPatterns: [string, RegExp][] = [
    ['Windows', /Windows NT ([\d.]+)/],
    ['macOS', /Mac OS X ([\d_.]+)/],
    ['iOS', /(?:iPhone|iPad|iPod).*? OS ([\d_]+)/],
    ['Android', /Android ([\d.]+)/],
    ['Chrome OS', /CrOS \S+ ([\d.]+)/],
    ['Linux', /Linux/],
  ]

  for (const [name, re] of osPatterns) {
    const m = ua.match(re)
    if (m) return { name, version: (m[1] ?? '').replace(/_/g, '.') }
  }

  return { name: '', version: '' }
}

function parseDevice(ua: string): { model: string; type: string; vendor: string } {
  if (/iPad/.test(ua)) return { model: 'iPad', type: 'tablet', vendor: 'Apple' }
  if (/iPhone/.test(ua)) return { model: 'iPhone', type: 'mobile', vendor: 'Apple' }
  if (/iPod/.test(ua)) return { model: 'iPod', type: 'mobile', vendor: 'Apple' }
  if (/Android/.test(ua) && /Mobile/.test(ua)) return { model: '', type: 'mobile', vendor: '' }
  if (/Android/.test(ua)) return { model: '', type: 'tablet', vendor: '' }
  return { model: '', type: '', vendor: '' }
}

function parseEngine(ua: string): { name: string; version: string } {
  const engines: [string, RegExp][] = [
    ['Blink', /Chrome\/([\d.]+)/],
    ['Gecko', /rv:([\d.]+)/],
    ['WebKit', /AppleWebKit\/([\d.]+)/],
  ]
  for (const [name, re] of engines) {
    const m = ua.match(re)
    if (m) return { name, version: m[1] ?? '' }
  }
  return { name: '', version: '' }
}

class BrowserLogger {
  private readonly _environment: Environment
  private readonly _userAgent: string
  private readonly _url: string
  private readonly _domain: string
  private readonly _service: string

  public constructor(service: string, domain: string = DEFAULT_DOMAIN) {
    this._environment = getEnvironment()
    this._userAgent = window.navigator.userAgent
    this._url = window.location.href
    this._domain = domain
    this._service = service
  }

  /**
   * Log a debug message
   * @param message The debug message to be logged
   * @param context Optional metadata to be logged
   */
  public async debug(message: string, context: Metadata = {}): Promise<void> {
    await this.logWithLevel(LogLevel.DEBUG, message, context)
  }

  /**
   * Log a warning message
   * @param message The warning message to be logged
   * @param context Optional metadata to be logged
   */
  public async warning(message: string, context: Metadata = {}): Promise<void> {
    await this.logWithLevel(LogLevel.WARNING, message, context)
  }

  /**
   * Log an error message
   * @param message The error message to be logged
   * @param context Optional metadata to be logged
   */
  public async error(message: string, context: Metadata = {}): Promise<void> {
    await this.logWithLevel(LogLevel.ERROR, message, context)
  }

  /**
   * Log an info message
   * @param message The info message to be logged
   * @param meta Optional metadata to be logged
   */
  public async info(message: string, context: Metadata = {}): Promise<void> {
    await this.logWithLevel(LogLevel.INFO, message, context)
  }

  private async logWithLevel(logLevel: LogLevel, message: string, context: Metadata): Promise<void> {
    await this.sendLog({
      message,
      level: logLevel,
      context,
    })
  }

  private async sendLog(payload: LogPayload): Promise<void> {
    const sanitizedUrl = encodeURIComponent(this._url)
    try {
      // Format log entry according to New Relic Log API specification
      // https://docs.newrelic.com/docs/logs/log-api/introduction-log-api/#json-content
      const logEntry = [
        {
          message: payload.message,
          timestamp: Date.now(),
          attributes: {
            'service.name': this._service,
            'labels.domain': this._domain,
            'vista.team_tag': TEAM_TAG,
            level: payload.level,
            url: sanitizedUrl,
            userAgent: this._userAgent,
            'browser.name': this._environment.browser.name,
            'browser.version': this._environment.browser.version,
            'device.model': this._environment.device.model,
            'device.type': this._environment.device.type,
            'device.vendor': this._environment.device.vendor,
            'os.name': this._environment.os.name,
            'os.version': this._environment.os.version,
            ...payload.context,
          },
        },
      ]

      const response = await fetch(`${LOG_FORWARDER_URL}log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logEntry),
      })

      if (!response.ok) {
        console.error('Error sending log', response.status)
      }
    } catch (error) {
      console.error('Unexpected error sending log', error)
    }
  }
}
class DevLogger implements ILogger {
  info(message: string, context?: Metadata): void {
    console.info(message, context)
  }
  error(message: string, context?: Metadata): void {
    console.error(message, context)
  }
  debug(message: string, context?: Metadata): void {
    console.debug(message, context)
  }
  warning(message: string, context?: Metadata): void {
    console.warn(message, context)
  }
}

class BrowserLoggerWrapper implements ILogger {
  private readonly _browserInstance: BrowserLogger
  constructor(service: string) {
    this._browserInstance = new BrowserLogger(service)
  }
  info(message: string, context?: Metadata): void {
    this._browserInstance.info(message, context).catch((error) => console.log('Error logging', error))
  }
  error(message: string, context?: Metadata): void {
    this._browserInstance.error(message, context).catch((error) => console.log('Error logging', error))
  }
  debug(message: string, context?: Metadata): void {
    this._browserInstance.debug(message, context).catch((error) => console.log('Error logging', error))
  }
  warning(message: string, context?: Metadata): void {
    this._browserInstance.warning(message, context).catch((error) => console.log('Error logging', error))
  }
}

export class Logger {
  private static _browserInstance: ILogger
  private constructor() {}

  public static get instance(): ILogger {
    Logger._browserInstance ??=
      typeof window === 'undefined' || ['staging.', 'localhost'].some((prefix) => window.location.hostname.startsWith(prefix))
        ? new DevLogger()
        : new BrowserLoggerWrapper(SERVICE_NAME)

    return Logger._browserInstance
  }
}
